import { ApiError, ClientErrorCode, apiErrorFromResponse } from '../api/errors';
import type { AuthResponse } from '../api/types';

import { getAuthState, useAuthStore } from './store';

/** Endpoint that exchanges the HttpOnly refresh cookie for a new access token (ARCHITECTURE §4.6). */
export const REFRESH_URL = '/api/auth/refresh';

/** Refresh this long before the access token expires. */
const REFRESH_AHEAD_MS = 60_000;
/** Treat tokens expiring within this window as already expired when sending requests. */
const EXPIRY_SKEW_MS = 10_000;
/** Automatic refreshes never follow each other faster than this (a misjudged expiry must not become a storm). */
const MIN_AUTO_REFRESH_INTERVAL_MS = 30_000;
/** Name of the lock serializing refreshes (and sign-outs) across browser tabs: refresh tokens rotate. */
const SESSION_LOCK = 'orbita-auth-refresh';
/** How long a localStorage lease (fallback without Web Locks) is valid if its tab dies while holding it. */
const LEASE_MS = 15_000;

let inflight: Promise<string | null> | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | undefined;

/**
 * Exchanges the refresh cookie for a new access token.
 *
 * - **Single-flight**: concurrent callers share one network request.
 * - **Cross-tab safe**: refresh tokens are rotated with reuse detection, so tabs must not refresh
 *   in parallel with the same cookie. A lock serializes them ({@link withSessionLock}); a tab that waited re-uses the
 *   rotated cookie the browser already stored.
 * - **Sign-out safe**: a refresh that was under way when the session ended does not restore it.
 *
 * Resolves to the new access token, or `null` when the session is gone (401) — in that case the
 * auth store is cleared. Rejects with `ApiError` on network/server failures (session kept).
 */
export function refreshAccessToken(): Promise<string | null> {
  inflight ??= withSessionLock(performRefresh).finally(() => {
    inflight = null;
  });
  return inflight;
}

/**
 * Runs `work` while no other refresh or sign-out of this browser runs (any tab). Uses the Web Locks API, which exists
 * only in secure contexts (HTTPS, localhost); on plain-HTTP intranet addresses a localStorage lease takes its place,
 * so restoring several tabs at once does not present one refresh token twice (which revokes the session).
 */
export async function withSessionLock<T>(work: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (locks) return locks.request(SESSION_LOCK, work);
  return withStorageLease(work);
}

interface Lease {
  owner: string;
  expires: number;
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

function readLease(): Lease | null {
  try {
    const value = localStorage.getItem(SESSION_LOCK);
    return value ? (JSON.parse(value) as Lease) : null;
  } catch {
    return null;
  }
}

async function withStorageLease<T>(work: () => Promise<T>): Promise<T> {
  const owner = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  let acquired = false;
  try {
    for (let attempt = 0; attempt < 200 && !acquired; attempt++) {
      const lease = readLease();
      if (!lease || lease.expires < Date.now()) {
        localStorage.setItem(SESSION_LOCK, JSON.stringify({ owner, expires: Date.now() + LEASE_MS }));
        // Tabs that raced for a free lease all wrote; the last write wins and only its owner goes ahead.
        await sleep(30);
        acquired = readLease()?.owner === owner;
      }
      if (!acquired) await sleep(50 + Math.random() * 100);
    }
  } catch {
    // Storage unavailable (blocked site data, private mode): nothing to coordinate with.
  }

  try {
    return await work();
  } finally {
    if (acquired && readLease()?.owner === owner) {
      try {
        localStorage.removeItem(SESSION_LOCK);
      } catch {
        // Ignore: the lease expires by itself.
      }
    }
  }
}

async function performRefresh(): Promise<string | null> {
  const generation = getAuthState().generation;
  let response: Response;
  try {
    response = await fetch(REFRESH_URL, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
  } catch (cause) {
    throw new ApiError({ status: 0, code: ClientErrorCode.network, message: 'Network error', cause });
  }

  if (getAuthState().generation !== generation) return null; // signed out meanwhile: discard the result

  if (response.status === 401 || response.status === 403) {
    const wasAuthenticated = getAuthState().status === 'authenticated';
    getAuthState().clear(wasAuthenticated ? 'expired' : null);
    return null;
  }
  if (!response.ok) throw await apiErrorFromResponse(response);

  const auth = (await response.json()) as AuthResponse;
  if (getAuthState().generation !== generation) return null;
  getAuthState().setSession(auth);
  return auth.accessToken;
}

/**
 * Returns an access token that is valid for at least a few more seconds, refreshing first when needed.
 * Returns null for anonymous sessions.
 */
export async function getValidAccessToken(): Promise<string | null> {
  const { status, accessToken, expiresAt } = getAuthState();
  if (status === 'anonymous') return null;
  if (accessToken && expiresAt !== null && expiresAt - Date.now() > EXPIRY_SKEW_MS) return accessToken;
  if (status === 'unknown' && !accessToken) return null;
  return refreshAccessToken();
}

/** Keeps the access token fresh: refreshes shortly before expiry and when a sleeping tab wakes up. */
export function startTokenAutoRefresh(): () => void {
  const schedule = () => {
    clearTimeout(refreshTimer);
    const { status, expiresAt } = getAuthState();
    if (status !== 'authenticated' || expiresAt === null) return;
    const delay = Math.max(expiresAt - Date.now() - REFRESH_AHEAD_MS, MIN_AUTO_REFRESH_INTERVAL_MS);
    refreshTimer = setTimeout(() => {
      refreshAccessToken().catch(() => {
        // Transient failure (offline/server down): retry soon; a 401 clears the session instead.
        refreshTimer = setTimeout(schedule, 15_000);
      });
    }, delay);
  };

  const onVisible = () => {
    if (document.visibilityState !== 'visible') return;
    const { status, expiresAt } = getAuthState();
    if (status === 'authenticated' && expiresAt !== null && expiresAt - Date.now() < REFRESH_AHEAD_MS) {
      void refreshAccessToken().catch(() => undefined);
    }
  };

  schedule();
  const unsubscribe = useAuthStore.subscribe((state, prev) => {
    if (state.expiresAt !== prev.expiresAt || state.status !== prev.status) schedule();
  });
  document.addEventListener('visibilitychange', onVisible);
  return () => {
    unsubscribe();
    clearTimeout(refreshTimer);
    document.removeEventListener('visibilitychange', onVisible);
  };
}
